/**
 * Root app flow:
 *   1. Resolve session (SWA `/.auth/me` or local mock).      → SignInScreen if none.
 *   2. Provide a configured SolandiaApi (HTTP or mock).
 *   3. Load `auth/me` → memberships.                          → Onboarding if none active.
 *   4. Provide FamilyContext and render the AppShell.
 */
import { useState } from 'react';
import { useI18n } from './i18n';
import { useSession } from './auth/useSession';
import { ApiProvider } from './api/ApiProvider';
import { FamilyProvider, useMe } from './family/FamilyContext';
import { AppShell } from './layout/AppShell';
import { SignInScreen } from './screens/SignInScreen';
import { FamilySetupWizard } from './screens/FamilySetupWizard';
import { ProfileSetupScreen } from './screens/ProfileSetupScreen';
import { LoadingScreen } from './components/LoadingScreen';
import type { SessionUser } from './auth/types';

/** localStorage flag: first-run profile setup has been completed/confirmed. */
const PROFILE_SETUP_DONE_KEY = 'solandio:profile-setup-done';

function isProfileSetupDone(): boolean {
  try {
    return window.localStorage.getItem(PROFILE_SETUP_DONE_KEY) === '1';
  } catch {
    // Storage unavailable (private mode): treat as "done" so we never trap the
    // user on a step we can't persist past.
    return true;
  }
}

function markProfileSetupDone(): void {
  try {
    window.localStorage.setItem(PROFILE_SETUP_DONE_KEY, '1');
  } catch {
    /* best-effort */
  }
}

export function App() {
  const { t } = useI18n();
  const { user, loading } = useSession();

  if (loading) return <LoadingScreen label={t.auth.loadingSession} />;

  if (!user) return <SignInScreen />;

  return (
    <ApiProvider user={user}>
      <AuthedApp user={user} />
    </ApiProvider>
  );
}

function AuthedApp({ user }: { user: SessionUser }) {
  const { t } = useI18n();
  const { data: me, loading, error, reload } = useMe();
  // Seed from localStorage so the step shows at most once per device. Held in
  // state so the successful-save transition re-renders past it without a reload.
  const [setupDone, setSetupDone] = useState(isProfileSetupDone);

  if (loading) return <LoadingScreen label={t.common.loading} />;

  if (error || !me) {
    return (
      <main className="auth-screen">
        <div className="card auth-card">
          <div className="banner banner-danger" role="alert">
            {t.errors.generic}
          </div>
          <button type="button" className="btn btn-ghost" onClick={reload} style={{ marginTop: 16 }}>
            {t.common.retry}
          </button>
        </div>
      </main>
    );
  }

  // First-run profile setup — comes AFTER `me` loads and BEFORE the no-family /
  // onboarding branch, so the order is: sign in → ProfileSetup → family
  // onboarding → AppShell (+ tour). Never fires on the sign-in screen because
  // we only reach here once authenticated. Shown once, then the flag sticks.
  if (!setupDone) {
    return (
      <ProfileSetupScreen
        initialName={me.user.displayName || user.displayName}
        reloadMe={reload}
        onDone={() => {
          markProfileSetupDone();
          setSetupDone(true);
        }}
      />
    );
  }

  const hasActiveFamily = me.memberships.some(
    (m) => m.membership.status === 'active',
  );

  // No family yet → run the normal-family setup wizard (FAMILY-PIVOT-UX §5):
  // create family → add kids → invite partner → (opt) invite grandparent →
  // seed a shopping list → land on a populated Home. The wizard creates the
  // family at step 1 and only `reload()`s `me()` once finished (or skipped to
  // the end), at which point `hasActiveFamily` flips and the AppShell mounts.
  // Same localStorage "done" gating model as ProfileSetupScreen: once an active
  // family exists, this branch never shows again.
  if (!hasActiveFamily) {
    return <FamilySetupWizard onDone={reload} />;
  }

  return (
    <FamilyProvider me={me} reloadMe={reload}>
      <AppShell user={user} />
    </FamilyProvider>
  );
}
